import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";

/**
 * Placeholder testimonials. Replace these with real customer reviews when ready.
 * Keep copy compliant — research-context only, no medical claims.
 */
const TESTIMONIALS = [
  {
    id: "t1",
    name: "Dr. Maya Chen",
    title: "Principal Investigator · University Lab",
    rating: 5,
    quote:
      "Documentation is exactly what a research-grade supplier should provide. COAs ship with every order and the vials arrive sealed, cold-chain intact.",
  },
  {
    id: "t2",
    name: "Jordan Park",
    title: "Lab Manager · Biotech Startup",
    rating: 5,
    quote:
      "We've cycled through three vendors before LuviaRx. Consistent purity, clean labelling, and reorder lead times that actually match what's quoted.",
  },
  {
    id: "t3",
    name: "Aisha Brooks, PhD",
    title: "Independent Research Group",
    rating: 5,
    quote:
      "The variant ranges and the new bac water sizing make small studies practical again. Support has been responsive without being pushy.",
  },
];

const StarRow = ({ rating }) => (
  <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`w-3.5 h-3.5 ${
          i < rating ? "text-sage-500 fill-sage-500" : "text-charcoal-200"
        }`}
        strokeWidth={1.5}
      />
    ))}
  </div>
);

export const TestimonialsCarousel = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);
  const count = TESTIMONIALS.length;

  // Auto-advance every 5s when not paused.
  useEffect(() => {
    if (paused) return undefined;
    timer.current = window.setInterval(() => {
      setIndex((i) => (i + 1) % count);
    }, 5000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [paused, count]);

  const go = (delta) => setIndex((i) => (i + delta + count) % count);

  return (
    <section
      data-testid="testimonials-carousel"
      className="bg-cream-200/60 border-y border-charcoal-100/50"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="container-x py-12 md:py-14">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="overline mb-1">Researcher Reviews</p>
            <h3 className="font-display text-xl md:text-2xl font-bold text-charcoal-600 leading-tight">
              Trusted by labs and independent researchers
            </h3>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <button
              type="button"
              onClick={() => go(-1)}
              data-testid="testimonial-prev"
              aria-label="Previous testimonial"
              className="w-9 h-9 rounded-full border border-charcoal-200 text-charcoal-500 hover:text-charcoal-700 hover:border-charcoal-400 flex items-center justify-center transition-colors"
            >
              <ChevronLeft className="w-4 h-4" strokeWidth={2} />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              data-testid="testimonial-next"
              aria-label="Next testimonial"
              className="w-9 h-9 rounded-full border border-charcoal-200 text-charcoal-500 hover:text-charcoal-700 hover:border-charcoal-400 flex items-center justify-center transition-colors"
            >
              <ChevronRight className="w-4 h-4" strokeWidth={2} />
            </button>
          </div>
        </div>

        <div className="relative overflow-hidden">
          <div
            className="flex transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
            style={{ transform: `translateX(-${index * 100}%)` }}
          >
            {TESTIMONIALS.map((t) => (
              <article
                key={t.id}
                data-testid={`testimonial-card-${t.id}`}
                className="w-full flex-shrink-0 pr-0 sm:pr-6"
              >
                <div className="bg-cream-100 rounded-3xl border border-charcoal-100/60 p-7 md:p-9 shadow-soft">
                  <StarRow rating={t.rating} />
                  <blockquote className="mt-5 font-display text-lg md:text-xl font-medium text-charcoal-600 leading-snug max-w-3xl">
                    “{t.quote}”
                  </blockquote>
                  <div className="mt-6 flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full bg-sage-100 text-sage-700 flex items-center justify-center font-display font-bold text-sm">
                      {t.name.charAt(0)}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-charcoal-600 leading-tight">{t.name}</p>
                      {t.title ? (
                        <p className="text-xs text-charcoal-400 leading-tight">{t.title}</p>
                      ) : null}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-center gap-1.5">
          {TESTIMONIALS.map((t, i) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setIndex(i)}
              data-testid={`testimonial-dot-${i}`}
              aria-label={`Show testimonial ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === index ? "w-8 bg-charcoal-500" : "w-1.5 bg-charcoal-200 hover:bg-charcoal-300"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsCarousel;
